"use client";

import * as React from "react";
import { TextField, Popover, InputAdornment, IconButton } from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import { DateDisplayFormat, ISODateOnly } from "./types";
import {
	completeDisplayDate,
	dateToISOByDisplayFormat,
	displayDateToISO,
	getCalendarFormatProps,
	isoToDisplayDate,
	isISOWithinBounds,
	isCalendarSelectionFinished,
	maskDateByFormat,
	pickerErrorToMessage,
} from "./utils";
import type { CalendarDateView, CalendarSelectionState } from "./utils";
import { isoDateOnlyToDate } from "@/utils/maskUtils";

export type SharedDateInputProps = {
	geralLabel?: React.ReactNode;
	name?: string;
	disabled?: boolean;
	placeholder?: string;
	displayFormat?: DateDisplayFormat;
	textFieldProps?: any;
	required?: boolean;
	isoValue: ISODateOnly | null;
	minISO: ISODateOnly | null;
	maxISO: ISODateOnly | null;
	otherISO?: ISODateOnly | null; // for range validation
	rangeRule?: "start" | "end"; // for range validation
	onCommit: (iso: ISODateOnly | null) => void;
	onCommitAlsoClearOther?: () => void; // for range validation
	rhfErrorMsg?: string;
	fieldError: unknown;
	setFieldError: (err: unknown) => void;
	ariaLabel?: string;
	/** Quando true (padrão), remove o botão do calendário da navegação sequencial por Tab. */
	skipAuxiliaryActionsTab?: boolean;
	/**
	 * Quando true (padrão), reserva a linha do helper text mesmo sem erro,
	 * evitando "pulo" de layout quando a validação aparece. Use false em
	 * barras de filtro para alinhar o campo com vizinhos sem helper.
	 */
	reserveHelperText?: boolean;
};

export function SharedDateInput({
	geralLabel,
	name,
	disabled = false,
	placeholder = "dd/mm/aaaa",
	displayFormat = "dd/mm/yyyy",
	textFieldProps,
	required,
	isoValue,
	minISO,
	maxISO,
	otherISO,
	rangeRule,
	onCommit,
	onCommitAlsoClearOther,
	rhfErrorMsg,
	fieldError,
	setFieldError,
	ariaLabel,
	skipAuxiliaryActionsTab = true,
	reserveHelperText = true,
}: Readonly<SharedDateInputProps>) {
	const minDate = React.useMemo(() => (minISO ? isoDateOnlyToDate(minISO) : null), [minISO]);
	const maxDate = React.useMemo(() => (maxISO ? isoDateOnlyToDate(maxISO) : null), [maxISO]);
	const selectedDate = React.useMemo(() => (isoValue ? isoDateOnlyToDate(isoValue) : null), [isoValue]);

	const [text, setText] = React.useState<string>(() => isoToDisplayDate(isoValue, displayFormat));

	// Popover
	const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
	const open = Boolean(anchorEl);
	const [calendarDate, setCalendarDate] = React.useState<Date | null>(selectedDate);

	// Sync quando valor ISO mudar externamente (reset do form etc.)
	React.useEffect(() => {
		const next = isoToDisplayDate(isoValue, displayFormat);
		setText(next);
		setFieldError(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [displayFormat, isoValue]);

	React.useEffect(() => {
		if (!open) setCalendarDate(selectedDate);
	}, [open, selectedDate]);

	function commitISO(nextISO: ISODateOnly | null) {
		setFieldError(null);

		// range guard (quando tem o outro lado)
		if (nextISO && otherISO && rangeRule) {
			if (rangeRule === "start" && nextISO > otherISO) {
				// start > end => limpa end
				onCommit(nextISO);
				onCommitAlsoClearOther?.();
				return;
			}
			if (rangeRule === "end" && nextISO < otherISO) {
				// end < start => limpa start
				onCommit(nextISO);
				onCommitAlsoClearOther?.();
				return;
			}
		}

		onCommit(nextISO);
	}

	function handleTextChange(e: React.ChangeEvent<HTMLInputElement>) {
		const raw = e.target.value;
		const masked = maskDateByFormat(raw, displayFormat, text);
		setText(masked);

		// limpou
		if (!masked) {
			commitISO(null);
			return;
		}

		// só comita quando completo
		const expectedLength = displayFormat === "mm/yyyy" ? 7 : 10;
		if (masked.length === expectedLength) {
			const isoOnly = displayDateToISO(masked, displayFormat);
			if (!isoOnly) {
				setFieldError("invalidDate");
				return;
			}

			const boundError = isISOWithinBounds(isoOnly, minISO, maxISO, displayFormat);
			if (boundError) {
				setFieldError(boundError);
				return;
			}

			commitISO(isoOnly);
			return;
		}

		// incompleto: não comita, não quebra digitação
		setFieldError(null);
	}

	function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
		textFieldProps?.onBlur?.(e);

		const expectedLength = displayFormat === "mm/yyyy" ? 7 : 10;
		if (text && text.length > 0 && text.length < expectedLength) {
			const completed = completeDisplayDate(text, displayFormat, minISO, maxISO);
			if (completed) {
				setText(completed);
				const iso = displayDateToISO(completed, displayFormat);
				commitISO(iso);
			} else setFieldError("invalidDate");
		}
	}

	function handleCalendarOpen(ev: React.MouseEvent<HTMLElement>) {
		setCalendarDate(selectedDate);
		setAnchorEl(ev.currentTarget);
	}

	function handleCalendarClose() {
		setCalendarDate(selectedDate);
		setAnchorEl(null);
	}

	function handlePick(d: Date | null, selectionState?: CalendarSelectionState, selectedView?: CalendarDateView) {
		setCalendarDate(d);

		if (!d) {
			setText("");
			commitISO(null);
			setAnchorEl(null);
			return;
		}

		if (!isCalendarSelectionFinished(displayFormat, selectionState, selectedView)) return;

		const isoDay = dateToISOByDisplayFormat(d, displayFormat);
		setText(isoToDisplayDate(isoDay, displayFormat));
		commitISO(isoDay);
		setAnchorEl(null);
	}

	const calendarFormatProps = React.useMemo(() => getCalendarFormatProps(displayFormat), [displayFormat]);

	const helper =
		rhfErrorMsg ??
		pickerErrorToMessage(
			fieldError,
			"A data final não pode ser menor que a inicial",
			"A data inicial não pode ser maior que a final",
		) ??
		textFieldProps?.helperText ??
		(reserveHelperText ? " " : undefined);

	return (
		<div>
			<TextField
				{...textFieldProps}
				disabled={disabled}
				label={geralLabel}
				name={name}
				value={text}
				onChange={handleTextChange}
				onBlur={handleBlur}
				error={!!rhfErrorMsg || fieldError != null}
				helperText={helper}
				fullWidth
				size="small"
				placeholder={placeholder}
				slotProps={{
					input: {
						inputMode: "numeric",
						endAdornment: (
							<InputAdornment position="end">
								<IconButton
									type="button"
									disabled={disabled}
									onClick={handleCalendarOpen}
									aria-label={ariaLabel ?? "Abrir calendário" + name}
									tabIndex={skipAuxiliaryActionsTab ? -1 : undefined}
								>
									<FuseSvgIcon>lucide:calendar</FuseSvgIcon>
								</IconButton>
							</InputAdornment>
						),
					},
					htmlInput: {
						...textFieldProps?.slotProps?.htmlInput,
						required: !!required,
					},
					...textFieldProps?.slotProps,
				}}
				sx={{
					"& .MuiInputBase-root": { minHeight: 32 },
					"& .MuiInputBase-input": {
						px: 1,
						py: 0.75,
						fontSize: "0.8125rem",
					},
					"& .MuiInputLabel-root": {
						fontSize: "0.75rem",
					},
					"& .MuiInputAdornment-root": { marginLeft: 0 },
					"& .MuiIconButton-root": { p: 0.5, color: "text.secondary" },
					...(textFieldProps?.sx ?? {}),
				}}
			/>

			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={handleCalendarClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
			>
				<div>
					<DateCalendar
						{...calendarFormatProps}
						value={calendarDate}
						onChange={(newValue, selectionState, selectedView) =>
							handlePick(newValue, selectionState, selectedView)
						}
						minDate={minDate ?? undefined}
						maxDate={maxDate ?? undefined}
					/>
				</div>
			</Popover>
		</div>
	);
}
